import { downloadSheet, postForm, postJson } from "./http";

/**
 * Opening balances: the grid, the staged figures, and the one write.
 *
 * Modelled on Zoho's own opening-balance form — six groups of accounts with a
 * debit and a credit column, receivable and payable above them, a running total
 * below — because a CA who has entered opening balances in Zoho Books should not
 * have to learn a second layout.
 */

export type BalanceSide = "debit" | "credit";

export interface OpeningBalanceAccountDTO {
  account_id: string;
  account_name: string;
  account_code: string | null;
  account_type: string;
  /** False only for an account switched off in Zoho that still carries a figure. */
  is_active: boolean;
  /** What will be sent. Null means no figure against this account. */
  side: BalanceSide | null;
  amount: number | null;
  /** What Zoho holds right now, so a changed cell can say what it is changing from. */
  zoho_side: BalanceSide | null;
  zoho_amount: number | null;
  /**
   * Only ever set on the receivable and payable rows: true once outstanding
   * documents exist on that side. A locked row's side/amount are the
   * documents' own total, it cannot be typed, and it stays OUT of the entry's
   * totals — the figure lives on the documents, not on the opening balance.
   */
  locked?: boolean;
}

/**
 * What the staged and pushed outstanding documents add up to, in base
 * currency. Their net (invoices − credit notes − bills + vendor credits) is
 * exactly what Zoho carries in Retained Earnings once they are pushed — the
 * figure the equity side must be entered NET of.
 */
export interface OutstandingTotalsDTO {
  receivable: number;
  payable: number;
  receivable_locked: boolean;
  payable_locked: boolean;
  invoice_documents: number;
  bill_documents: number;
}

/**
 * One of the client's GST registrations — a Zoho Location, and the scope of
 * one opening-balance save.
 *
 * `location_id` is Zoho's own branch id, which is what its endpoint takes.
 * A client with one registration gets a single-item list and no choice to make.
 */
export interface OpeningBalanceRegistrationDTO {
  location_id: string;
  name: string;
  gstin: string | null;
  is_primary: boolean;
}

export interface OpeningBalanceGroupDTO {
  key: string;
  label: string;
  accounts: OpeningBalanceAccountDTO[];
}

export interface OpeningBalanceReadbackDTO {
  status: string;
  row_count: number | string;
  fetched_at: string | null;
  error: string | null;
}

export interface OpeningBalanceSheetDTO {
  /**
   * yyyy-mm-dd. Shown as "Account start date", which is what the Start date
   * step calls it; the key keeps Zoho's own name for the field because that is
   * what the API takes, and because `account_start_date` below is a different
   * thing — the client's answer, kept so the screen can say where this came
   * from. Zoho holds exactly one of these per organisation.
   */
  migration_date: string | null;
  /** Where that date came from, so the screen can say so rather than imply it was typed. */
  date_source: "entered" | "account-start-date";
  account_start_date: string | null;
  base_currency: { currency_id: string; currency_code: string } | null;
  /**
   * Zoho keeps receivable and payable out of the account grid and takes one
   * figure for each. Null only if the client's chart of accounts somehow has
   * neither, which Zoho does not allow.
   */
  receivable: OpeningBalanceAccountDTO | null;
  payable: OpeningBalanceAccountDTO | null;
  outstanding: OutstandingTotalsDTO;
  /**
   * Every registration this client can file against, primary first, and the
   * one this sheet is showing. The grid is ONE registration's figures — Zoho's
   * own Opening Balances screen works the same way, a location at a time — so
   * `groups`, `totals` and the receivable/payable rows all belong to
   * `location_id` and nothing else.
   */
  registrations: OpeningBalanceRegistrationDTO[];
  location_id: string | null;
  /**
   * Where every registration stands, not just the one on screen — the grid
   * shows one at a time, and a branch left half-entered has to stop the step
   * as surely as the one being looked at. As fresh as the last accepted save.
   *
   * `has_lines` separates "nothing entered yet" from "entered and agrees":
   * both read as a zero difference.
   */
  registration_totals: {
    location_id: string;
    name: string;
    debit: number;
    credit: number;
    difference: number;
    balanced: boolean;
    has_lines: boolean;
  }[];
  groups: OpeningBalanceGroupDTO[];
  totals: {
    debit: number;
    credit: number;
    /**
     * Debits minus credits, for THIS registration. The push is refused while
     * any registration's difference is not zero — Zoho plugs each location
     * separately, so one branch's surplus does not excuse another's shortfall.
     */
    difference: number;
    balanced: boolean;
  };
  zoho: {
    opening_balance_id: string | null;
    date: string | null;
    total: number | null;
    /**
     * Whether the organisation already holds an opening balance. Once true,
     * every push REPLACES it — Zoho's update endpoint takes the whole entry.
     */
    entered: boolean;
  };
  pushed_at: string | null;
  readback: {
    ledgers: OpeningBalanceReadbackDTO | null;
    opening_balance: OpeningBalanceReadbackDTO | null;
  };
  /** Only on a push response. */
  push?: OpeningBalancePushDTO;
}

export interface OpeningBalancePushDTO {
  sent: number;
  /** One per GST registration that had lines to send. */
  saves: number;
  opening_balance_id: string;
  /** False when this replaced an entry that was already there. */
  created: boolean;
}

/** One cell as the CA typed it. An empty amount clears the account's figure. */
export interface OpeningBalanceEdit {
  account_id: string;
  side: BalanceSide;
  amount: string;
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// The server puts the readable reason under a fixed key — "debits and
// credits differ by 40,000.00" rather than "HTTP 400".
const balancesCall = <T>(path: string, body: unknown): Promise<T> =>
  postJson<T>(path, body, ["opening_balance"]);

/**
 * Everything the step renders, in one read. No Zoho call.
 *
 * One GST registration's figures. Omit `locationId` for the primary, which is
 * the whole answer for a client with one registration.
 */
export const fetchOpeningBalances = async (
  publicId: string,
  locationId?: string | null
): Promise<OpeningBalanceSheetDTO> =>
  (
    await balancesCall<Envelope<OpeningBalanceSheetDTO>>("/api/onboarding/opening-balances/sheet", {
      public_id: publicId,
      ...(locationId ? { location_id: locationId } : {}),
    })
  ).data;

/**
 * Record what a CA typed. NOTHING IS SENT.
 *
 * Returns the whole sheet, so the running total and the difference move in the
 * same render as the cell that changed.
 */
export const saveOpeningBalances = async (
  publicId: string,
  rows: OpeningBalanceEdit[],
  migrationDate?: string | null,
  locationId?: string | null
): Promise<OpeningBalanceSheetDTO> =>
  (
    await balancesCall<Envelope<OpeningBalanceSheetDTO>>("/api/onboarding/opening-balances/save", {
      public_id: publicId,
      rows,
      ...(migrationDate === undefined ? {} : { migration_date: migrationDate ?? "" }),
      // The registration the grid is showing. Every row in this request belongs
      // to it; the bulk import is the one caller that names a location per row.
      ...(locationId ? { location_id: locationId } : {}),
    })
  ).data;

/**
 * Send the entry to the client's Zoho Books.
 *
 * THIS WRITES, AND IT REPLACES WHAT IS THERE. Zoho's update endpoint takes the
 * whole opening balance rather than a patch, so the server sends every staged
 * figure on every push. One call — unlike the contact and ledger pushes there
 * is nothing to page through.
 *
 * Refused while debits and credits differ. Zoho itself would accept an
 * unbalanced entry and post the difference to its own Opening Balance
 * Adjustments account.
 */
export const pushOpeningBalances = async (
  publicId: string,
  locationId?: string | null
): Promise<OpeningBalanceSheetDTO> =>
  (
    await balancesCall<Envelope<OpeningBalanceSheetDTO>>("/api/onboarding/opening-balances/push", {
      public_id: publicId,
      // Every registration is sent whatever this says; it only picks which
      // registration's grid comes back.
      ...(locationId ? { location_id: locationId } : {}),
    })
  ).data;

// ── The bulk sheet ──────────────────────────────────────────────────────────

/** One row the review refused or skipped, with the sheet row it was on. */
export interface OpeningBalanceSheetIssue {
  sheet_row_no: number;
  account: string;
  reason: string;
}

/** A row the review accepted, sent back verbatim on confirm. */
export interface OpeningBalanceSheetRow {
  account_id: string;
  side: BalanceSide | null;
  amount: number | null;
}

export interface OpeningBalanceSheetPreviewDTO {
  counts: {
    rows_read: number;
    to_apply: number;
    unchanged: number;
    to_clear: number;
    skipped: number;
    refused: number;
  };
  refused: OpeningBalanceSheetIssue[];
  skipped: OpeningBalanceSheetIssue[];
  /** What the grid's totals would read after confirming — the primary's. */
  totals: { debit: number; credit: number; difference: number; balanced: boolean };
  /**
   * The same, one per GST registration, and empty for a client with only one.
   * The push gate is per registration, so a sheet whose branches cancel each
   * other out is still a sheet that cannot be sent.
   */
  totals_by_registration: {
    location_id: string | null;
    name: string;
    totals: { debit: number; credit: number; difference: number; balanced: boolean };
  }[];
  rows: OpeningBalanceSheetRow[];
  organisation: string;
}

/** The grid as a spreadsheet, staged figures already in it. */
export const downloadOpeningBalanceTemplate = (publicId: string): Promise<void> =>
  downloadSheet("/api/onboarding/opening-balances/template", { public_id: publicId }, "ledger balances.xlsx");

/**
 * Read a filled-in sheet and answer with what confirming it would do.
 * WRITES NOTHING — the review is the whole response.
 */
export const uploadOpeningBalanceSheet = async (
  publicId: string,
  file: File
): Promise<OpeningBalanceSheetPreviewDTO> => {
  const form = new FormData();
  form.append("public_id", publicId);
  form.append("file", file);

  return (
    await postForm<Envelope<OpeningBalanceSheetPreviewDTO>>(
      "/api/onboarding/opening-balances/import/upload",
      form,
      ["file"]
    )
  ).data;
};

/**
 * Stage the reviewed rows. Still local — the entry reaches Zoho only from the
 * screen's own Send button. The server re-checks every row.
 */
export const applyOpeningBalanceSheet = async (
  publicId: string,
  rows: OpeningBalanceSheetRow[]
): Promise<OpeningBalanceSheetDTO> =>
  (
    await balancesCall<Envelope<OpeningBalanceSheetDTO>>("/api/onboarding/opening-balances/import/apply", {
      public_id: publicId,
      rows,
    })
  ).data;
